import { useContext, useState, useEffect, useMemo } from "react";
import {AppState} from "../lib/context/AppProvider";
import Table from "react-bootstrap/Table";
import Header from "../components/Header";
import { fixDate, getRelativeTime } from "../utils/time";
import { Button, Modal, Spinner, Form } from "react-bootstrap";
import { FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";

const dateRegex = /^\d\d\d\d-\d\d-\d\d([T ]\d\d:\d\d(:\d\d(.\d+)?)?)?Z?$/;

export const AddWhitelistModal = ({show, onHide, setWhitelistConfirmModalOptions}) => {
    const [emails, setEmails] = useState(null);
    const [reason, setReason] = useState(null);
    const [expiration, setExpiration] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if(show) {
            setEmails(null);
            setReason(null);
            setExpiration(null);
            setError(false);
        }
    }, [show]);

    const doWhitelist = () => {
        setWhitelistConfirmModalOptions({
            emails: [...new Set(
                emails.split(/[\n,]/)
                    .map((e) => e.trim().toLowerCase())
                    .filter((e) => e.length > 0)
                )],
            reason: reason?.length ? reason.trim() : null,
            expiration: expiration?.length ? new Date(fixDate(expiration.trim())).getTime() : null,
        });
    };

    const valid = useMemo(() => {
        setError(null);
        if(emails) {
            if(!(expiration?.length)) return true;
            if(dateRegex.test(expiration.trim())) return true;
            setError("Invalid expiration datetime");
        }
        return false;
    }, [emails, expiration]);

    return (
        <Modal show={show} onHide={onHide} size="lg"
            className="customscrollbar"
            style={{
                maxHeight: "calc(100vh - 28px - 28px)",
                overflowY: "scroll"
            }}
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>
                    Whitelist Additional Emails
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if(valid) {
                            doWhitelist();
                        }
                    }}
                >
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="whitelist-emails">Emails <span style={{color: "#AAAAAA"}}>(comma or newline separated)</span></Form.Label>
                        <Form.Control
                            autoFocus
                            id="whitelist-emails"
                            type="text"
                            as="textarea" rows={3}
                            value={emails ?? ""}
                            onChange={(e) => setEmails(e.target.value)}
                            className="bg-dark text-white customscrollbar"
                            aria-label="Emails"
                            style={{resize: "none"}}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="whitelist-reason">Reason</Form.Label>
                        <Form.Control
                            id="whitelist-reason"
                            type="text"
                            value={reason ?? ""}
                            onChange={(e) => setReason(e.target.value)}
                            className="bg-dark text-white"
                            aria-label="Reason"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="whitelist-expires">Expiration</Form.Label>
                        <Form.Control
                            id="whitelist-expires"
                            type="text"
                            value={expiration ?? ""}
                            onChange={(e) => setExpiration(e.target.value)}
                            className="bg-dark text-white"
                            aria-label="Expiration"
                        />
                    </Form.Group>
                </Form>

                {error && (<p style={{color: "red"}}>{error}</p>)}
                
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button variant="secondary" onClick={onHide} data-testid="whitelist-close">
                    Close
                </Button>
                <Button 
                    data-testid="whitelist"
                    variant="primary"
                    onClick={() => doWhitelist()}
                    disabled={!valid}
                >
                    Whitelist
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const WhitelistConfirmationModal = ({options, onHide, onSuccess, setWhitelistDirty}) => {
    const [working, setWorking] = useState(false);
    const [shownOptions, setShownOptions] = useState(null);
    const {postData} = useContext(AppState);
    
    useEffect(() => {
        if(options) {
            const opts = {reason: options.reason, expiration: options.expiration, domains: [], mailServers: [], full: []};
            for(const email of options.emails) {
                if(email.startsWith(".")) {
                    opts.domains.push(email);
                }
                else if(!email.includes("@")) {
                    opts.mailServers.push(email);
                }
                else {
                    opts.full.push(email);
                }
            }
            setShownOptions(opts);
        }
    }, [options]);

    return shownOptions && (
        <Modal 
            show={options} 
            onHide={onHide}
            size="lg"
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>Confirm Whitelist</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                <p>Are you sure you want to whitelist the following?</p>
                <br />
                <p><b>Domains ({shownOptions.domains.length}):</b> {shownOptions.domains.length ? shownOptions.domains.join(", ") : "None"}</p>
                <p><b>Mail Servers ({shownOptions.mailServers.length}):</b> {shownOptions.mailServers.length ? shownOptions.mailServers.join(", ") : "None"}</p>
                <p><b>Emails ({shownOptions.full.length}):</b> {shownOptions.full.length ? shownOptions.full.join(", ") : "None"}</p>

                <br />
                <p><b>Reason:</b> {shownOptions.reason ? shownOptions.reason : "None"}</p>

                <br />
                <p><b>Expiration:</b> {shownOptions.expiration ? `${new Date(shownOptions.expiration).toISOString()} (${getRelativeTime(new Date(shownOptions.expiration).toISOString())})` : "None"}</p>
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button 
                    variant="secondary" 
                    onClick={onHide}
                >
                    Cancel
                </Button>
                <Button 
                    variant="danger"
                    onClick={async () => {
                        setWorking(true);
                        
                        const payload = {
                            domains: shownOptions.domains,
                            mailservers: shownOptions.mailServers,
                            emails: shownOptions.full
                        };
                        if(shownOptions.reason) payload.reason = shownOptions.reason;
                        if(shownOptions.expiration) payload.expiration = new Date(shownOptions.expiration).getTime();

                        const response = await postData("email-whitelist/insert", payload, "POST");

                        if(response) {
                            const json = await response.json();
                            if(json.message) {
                                toast.error(json.message);
                            }
                            if(json.message && !json.message.includes("already whitelisted")) {
                                onHide();
                            }
                            else {
                                onSuccess();
                                setWhitelistDirty(true);
                            }
                        }
                        else onHide();

                        setWorking(false);
                    }}
                    disabled={working}
                >
                    Whitelist {working && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ConfirmationModal = ({options, onHide, setWhitelistDirty}) => {
    const [working, setWorking] = useState(false);
    const [shownOptions, setShownOptions] = useState(null);
    const {postData} = useContext(AppState);
    
    useEffect(() => {
        if(options) {
            setShownOptions(options);
        }
    }, [options]);

    return shownOptions && (
        <Modal 
            show={options} 
            onHide={onHide}
            size="md"
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>Confirm Unwhitelist</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                Are you sure you want to unwhitelist the {shownOptions.type.toLowerCase()} <b>{shownOptions.id}</b>?
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button 
                    variant="secondary" 
                    onClick={onHide}
                >
                    Cancel
                </Button>
                <Button 
                    variant="danger"
                    onClick={async () => {
                        setWorking(true);
                        
                        const payload = {
                            type: shownOptions.type,
                            value: shownOptions.id,
                        };

                        const response = await postData("email-whitelist/remove", payload, "POST");
                        if(response) {
                            const json = await response.json();
                            if(json.message) {
                                toast.error(json.message);
                            }
                            else {
                                setWhitelistDirty(true);
                            }
                        }

                        setWorking(false);
                        onHide();
                    }}
                    disabled={working}
                >
                    Unwhitelist {working && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const WhitelistTable = ({type, whitelist, setConfirmModalOptions}) => {
    return (
        <div className="login-table-container">
            <div className="login-table customscrollbar">
                <Table
                    striped
                    bordered
                    hover
                    size="sm"
                    variant="dark"
                    key={3}
                    style={{textAlign: "center"}}
                >
                    <thead className = "login-table-head">
                        <tr>
                            <th style={{width: "32%"}}>{type}</th>
                            <th style={{width: "32%"}}>Reason</th>
                            <th style={{width: "32%"}}>Expiration</th>
                            <th style={{width: "4%"}}></th>
                        </tr>
                    </thead>
                    <tbody style={{overflowY: "scroll"}}>
                        {whitelist && whitelist.map((row) => {
                            return (
                                <tr
                                    key={row.Id}
                                    className="table-row"
                                >
                                    <td className="login-table-cell horizontal-scroll-overflow hidden-scrollbar">
                                        {row.Id}
                                    </td>
                                    <td className="login-table-cell horizontal-scroll-overflow hidden-scrollbar"
                                        style={{maxWidth: "120px"}}>
                                            {row.Reason ?? "None"}
                                    </td>
                                    <td className="login-table-cell horizontal-scroll-overflow hidden-scrollbar">
                                        {row.ExpiresAt ? `${row.ExpiresAt} (${getRelativeTime(row.ExpiresAt)})` : "None"}
                                    </td>
                                    <td className="login-table-cell">
                                        <center>
                                            <Button variant="danger" size="sm" onClick={() => {
                                                setConfirmModalOptions({type: type, id: row.Id})
                                            }}>
                                                <FaRegTrashAlt />
                                            </Button>
                                        </center>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
        );
};

async function fetchData(getData, setWhitelist) {
    const rows = await getData("email-whitelist");
    setWhitelist({
        domain: rows.filter((row) => row.Domain).map((row) => ({...row, Id: row.Domain})),
        mailServer: rows.filter((row) => row.MailServer).map((row) => ({...row, Id: row.MailServer})),
        full: rows.filter((row) => row.Full).map((row) => ({...row, Id: row.Full})),
    });
}

function EmailWhitelist() {
    const {user, getData } = useContext(AppState);
    const [confirmModalOptions, setConfirmModalOptions] = useState(false);
    const [whitelistConfirmModalOptions, setWhitelistConfirmModalOptions] = useState(false);
    
    const [whitelist, setWhitelist] = useState({domain: [], mailServer: [], full: []});
    const [showModal, setShowModal] = useState(false);

    const [whitelistDirty, setWhitelistDirty] = useState(false);

    useEffect(() => {
        fetchData(getData, setWhitelist);
    }, [user, getData]);

    useEffect(() => {
        if(whitelistDirty) {
            fetchData(getData, setWhitelist);
            setWhitelistDirty(false);
        }
    }, [whitelistDirty, getData]);

    const handleShowModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    return (  
        <>
            <Header
                showSideBarMenu={true}
                showDashboardMenu={false}
                height={56} 
                className="pb-5"
            />
            <div className="page-background customscrollbar">
                <section className="settings-section">
                    <h4 className="settings-section-title">Email Domain Whitelist</h4>
                    <hr style={{ borderColor: '#495057' }}/>
                    
            
                    <WhitelistTable type="Domain" whitelist={whitelist?.domain}
                        setConfirmModalOptions={setConfirmModalOptions} />
                </section>
                <section className="settings-section">
                    <h4 className="settings-section-title">Email Mail Server Whitelist</h4>
                    <hr style={{ borderColor: '#495057' }}/>
            
                    <WhitelistTable type="Mail Server" whitelist={whitelist?.mailServer}
                        setConfirmModalOptions={setConfirmModalOptions} />
                </section>
                <section className="settings-section">
                    <h4 className="settings-section-title">Email Address Whitelist</h4>
                    <hr style={{ borderColor: '#495057' }}/>
            
                    <WhitelistTable type="Email" whitelist={whitelist?.full}
                        setConfirmModalOptions={setConfirmModalOptions} />
                </section>

                <Button
                    variant="outline-warning"
                    className="btn-accounts mb-5"
                    onClick={handleShowModal}
                >
                    Whitelist Additional Emails
                </Button>
            </div>

            <ConfirmationModal
                setWhitelistDirty={setWhitelistDirty}
                options={confirmModalOptions}
                onHide={() => setConfirmModalOptions(null)}
            />

            <AddWhitelistModal
                show={showModal}
                onHide={handleCloseModal}
                setWhitelistConfirmModalOptions={setWhitelistConfirmModalOptions}
            />

            <WhitelistConfirmationModal
                setWhitelistDirty={setWhitelistDirty}
                options={whitelistConfirmModalOptions}
                onHide={() => setWhitelistConfirmModalOptions(null)}
                onSuccess={() => {
                    setWhitelistConfirmModalOptions(null);
                    handleCloseModal();
                }}
            />
        </>
    );
}

export default EmailWhitelist;
